import { ALL_FILTER_VALUE } from '@/container/organization/atoms/organizationAtoms';
import { useCombinedOrganizationInfo } from '@/hooks/query/useCombinedOrganizationInfo';
import { Button } from '@heidi/design-system/button';
import { Checkbox } from '@heidi/design-system/checkbox';
import { ComboboxTrigger } from '@heidi/design-system/combobox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@heidi/design-system/command';
import { Popover, PopoverContent, PopoverTrigger } from '@heidi/design-system/popover';
import { Separator } from '@heidi/design-system/separator';
import { UsersRound } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

interface TeamsSelectionProps {
  selectedValues?: string[];
  setSelectedValues?: (values: string[]) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  creatorTeam?: string;
}

export const TeamsSelection = (props: TeamsSelectionProps) => {
  const { selectedValues, setSelectedValues, open, setOpen, creatorTeam } = props;
  const intl = useIntl();
  const { orgTeams } = useCombinedOrganizationInfo();
  const [selectedItemsSnapshot, setSelectedItemsSnapshot] = useState<string[]>([]);
  const allFilterValue = ALL_FILTER_VALUE;

  const pinnedOptions = useMemo(() => {
    return orgTeams?.filter(
      (orgTeam) =>
        selectedItemsSnapshot.includes(orgTeam.id) && orgTeam.id !== allFilterValue
    );
  }, [orgTeams, selectedItemsSnapshot, allFilterValue]);

  const unpinnedOptions = useMemo(() => {
    return orgTeams?.filter(
      (orgTeam) =>
        !selectedItemsSnapshot.includes(orgTeam.id) && orgTeam.id !== allFilterValue
    );
  }, [orgTeams, selectedItemsSnapshot, allFilterValue]);

  const isTeamSelected = useCallback(
    (teamId: string) => {
      return selectedValues?.includes(teamId);
    },
    [selectedValues]
  );

  const handleTeamSelection = useCallback(
    (teamId: string, checked: boolean) => {
      if (checked) {
        setSelectedValues?.([...(selectedValues ?? []), teamId]);
      } else {
        setSelectedValues?.(selectedValues?.filter((v) => v !== teamId) ?? []);
      }
    },
    [selectedValues, setSelectedValues]
  );

  const getDisplayText = useCallback(() => {
    if (selectedValues && selectedValues.length > 1) {
      return intl.formatMessage(
        {
          defaultMessage:
            '{teams, plural, one {# team selected} other {# teams selected}}',
          id: '3jhQ2F/Aqc',
          description: 'Number of teams selected in the visibility settings',
        },
        { teams: selectedValues.length }
      );
    } else if (selectedValues && selectedValues.length === 1) {
      return orgTeams?.find((orgTeam) => orgTeam.id === selectedValues[0])?.name;
    }
    return intl.formatMessage({
      defaultMessage: 'Select teams',
      id: 'uwY2B7bYSR',
      description: 'Placeholder inviting the user to select teams',
    });
  }, [intl, orgTeams, selectedValues]);

  useEffect(() => {
    if (!open) {
      setSelectedItemsSnapshot([...(selectedValues ?? [])]);
    }
  }, [open, selectedValues]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <ComboboxTrigger
          open={open}
          leadingIcon={<UsersRound />}
          isPlaceholder={!selectedValues?.length}
          role="combobox"
        >
          {getDisplayText()}
        </ComboboxTrigger>
      </PopoverTrigger>
      <PopoverContent className="w-[380px] p-0" align="start">
        <Command className="rounded-md">
          {/* Search */}
          <CommandInput
            placeholder={intl.formatMessage({
              defaultMessage: 'Search for a team',
              id: 'nU2p76IGV7',
              description: 'Placeholder for the team search field',
            })}
            className="border-0 focus:ring-0"
          />

          <CommandList>
            <CommandEmpty>
              {intl.formatMessage({
                defaultMessage: 'No results found',
                id: 'MGb3SKKeO/',
                description: 'Empty state when no teams match the search',
              })}
            </CommandEmpty>

            {/* Selected teams section - shown at top when there are selected items */}
            {pinnedOptions && pinnedOptions.length > 0 && (
              <>
                <CommandGroup>
                  <div>
                    <div className="space-y-2">
                      {/* Selected team options */}
                      {pinnedOptions.map((orgTeam) => (
                        <CommandItem
                          key={orgTeam.id}
                          value={orgTeam.name}
                          onSelect={() => {}}
                          className="flex items-center justify-between gap-1"
                        >
                          <div
                            key={orgTeam.id}
                            className="flex w-full items-center gap-2"
                          >
                            <Checkbox
                              id={orgTeam.id}
                              checked={isTeamSelected(orgTeam.id)}
                              onCheckedChange={(checked) =>
                                handleTeamSelection(orgTeam.id, checked as boolean)
                              }
                              disabled={creatorTeam === orgTeam.id}
                            />
                            <label
                              htmlFor={orgTeam.id}
                              className="w-full cursor-pointer text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              {orgTeam.name}
                            </label>
                          </div>
                        </CommandItem>
                      ))}
                    </div>
                  </div>
                  <Separator />
                </CommandGroup>
              </>
            )}
            {/* Unselected teams section */}
            {unpinnedOptions && unpinnedOptions.length > 0 && (
              <>
                <CommandGroup>
                  <div>
                    <div className="space-y-2">
                      {unpinnedOptions.map((orgTeam) => (
                        <CommandItem
                          key={orgTeam.id}
                          value={orgTeam.name}
                          onSelect={() => {
                            // onSelect(
                            //   option.id === selectedValue ? allFilterValue : option.id
                            // );
                            // setOpen(false);
                          }}
                          className="flex items-center justify-between gap-1"
                        >
                          <div
                            key={orgTeam.id}
                            className="flex w-full items-center gap-2"
                          >
                            <Checkbox
                              id={orgTeam.id}
                              checked={isTeamSelected(orgTeam.id)}
                              onCheckedChange={(checked) =>
                                handleTeamSelection(orgTeam.id, checked as boolean)
                              }
                              disabled={orgTeam.id === creatorTeam}
                            />
                            <label
                              htmlFor={orgTeam.id}
                              className="w-full cursor-pointer text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              {orgTeam.name}
                            </label>
                          </div>
                        </CommandItem>
                      ))}
                    </div>
                  </div>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>

        {/* Clear button */}
        {selectedValues && selectedValues.length > 0 && (
          <div className="flex justify-end border-t p-2">
            <Button
              disableAutomaticSvgSizing
              variant="ghost"
              size="sm"
              onClick={() => setSelectedValues?.([])}
              className="text-text-secondary"
            >
              {intl.formatMessage({
                defaultMessage: 'Clear',
                id: 'hm1DeXX+si',
                description: 'Action to clear all selected teams',
              })}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
